import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, CheckCircle2, ExternalLink } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";

const blankPlan = { name: "", price: "", suits: "" };
const blankFaq = { q: "", a: "" };

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">{label}</span>
    {children}
    {hint && <span className="block mt-1 text-xs text-slate-400">{hint}</span>}
  </label>
);

const input = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-orange focus:outline-none";

export default function XeroPricingManager() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.xeroPricing.get().then(setCfg).catch(() => toast.error("Could not load Xero prices")); }, []);
  if (!cfg) return <div className="text-slate-400 text-sm">Loading…</div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const setPlan = (i, k, v) => set("plans", cfg.plans.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const setFaq = (i, k, v) => set("faqs", cfg.faqs.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));
  const guide = cfg.plan_guide || {};
  const setGuide = (k, v) => set("plan_guide", { ...guide, [k]: v });
  const setStep = (i, k, v) => setGuide("steps", (guide.steps || []).map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  const save = async () => {
    setSaving(true);
    try {
      // Saving is itself a confirmation that the prices are current.
      const data = { ...cfg, checked_on: new Date().toISOString().slice(0, 10) };
      setCfg(await adminApi.xeroPricing.save(data));
      toast.success("Saved — prices marked as checked today");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const confirm = async () => {
    try {
      const { checked_on } = await adminApi.xeroPricing.confirm();
      setCfg({ ...cfg, checked_on });
      toast.success("Confirmed — the date on the page has moved to today");
    } catch { toast.error("Could not confirm"); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="xero-pricing-manager">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="font-display font-bold text-xl text-navy">Xero prices</h2>
          <p className="text-sm text-slate-500 mt-1">
            Shown on the Xero Subscription service page. Last checked{" "}
            <strong data-testid="xero-checked-on">{cfg.checked_on || "never"}</strong>.
          </p>
        </div>
        <div className="flex gap-2">
          <a href="https://www.xero.com/uk/pricing-plans/" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 border border-slate-200 hover:border-orange text-navy font-semibold text-sm px-4 py-2.5 rounded-full transition-colors">
            Xero's prices <ExternalLink size={14} />
          </a>
          <button type="button" onClick={confirm} data-testid="xero-confirm-btn"
                  className="inline-flex items-center gap-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold text-sm px-4 py-2.5 rounded-full transition-colors">
            <CheckCircle2 size={15} /> Prices still correct
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid="xero-save-btn"
                  className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors">
            <Save size={15} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <label className="flex items-center gap-3 text-sm font-semibold text-navy">
        <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)}
               data-testid="xero-enabled" className="w-4 h-4 accent-orange" />
        Show the cost answer on the page
      </label>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Page title (search result)"><input className={input} value={cfg.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} data-testid="xero-seo-title" /></Field>
        <Field label="Service page slug"><input className={input} value={cfg.service_slug || ""} onChange={(e) => set("service_slug", e.target.value)} /></Field>
      </div>
      <Field label="Meta description" hint="Aim for under 160 characters. No discount percentage.">
        <textarea className={input} rows={2} value={cfg.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} data-testid="xero-seo-desc" />
      </Field>
      <Field label="Heading"><input className={input} value={cfg.heading || ""} onChange={(e) => set("heading", e.target.value)} /></Field>
      <Field label="The answer" hint="Answer the question in the first sentence.">
        <textarea className={input} rows={4} value={cfg.answer || ""} onChange={(e) => set("answer", e.target.value)} data-testid="xero-answer" />
      </Field>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-navy">Plans</h3>
          <button type="button" onClick={() => set("plans", [...cfg.plans, { ...blankPlan }])}
                  className="inline-flex items-center gap-1.5 text-orange font-semibold text-sm"><Plus size={15} /> Add plan</button>
        </div>
        <div className="space-y-2">
          {(cfg.plans || []).map((p, i) => (
            <div key={i} className="grid gap-2 md:grid-cols-[1fr,7rem,2fr,auto] items-start" data-testid={`xero-plan-row-${i}`}>
              <input className={input} placeholder="Plan" value={p.name || ""} onChange={(e) => setPlan(i, "name", e.target.value)} />
              <input className={input} placeholder="£39" value={p.price || ""} onChange={(e) => setPlan(i, "price", e.target.value)} />
              <input className={input} placeholder="Who it suits" value={p.suits || ""} onChange={(e) => setPlan(i, "suits", e.target.value)} />
              <button type="button" onClick={() => set("plans", cfg.plans.filter((_, idx) => idx !== i))}
                      className="p-2.5 text-slate-400 hover:text-red-500 transition-colors" aria-label="Remove plan"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-400">"+ VAT" is added to every price automatically.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="VAT note"><input className={input} value={cfg.vat_note || ""} onChange={(e) => set("vat_note", e.target.value)} /></Field>
        <Field label="Price change note"><input className={input} value={cfg.price_note || ""} onChange={(e) => set("price_note", e.target.value)} /></Field>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="CASS discount %" hint="The Through CASS column is Xero's price less this. Blank or 0 hides the column.">
          <input className={input} type="number" min="0" max="100" step="1" value={cfg.cass_discount_pct ?? ""}
                 onChange={(e) => set("cass_discount_pct", e.target.value === "" ? "" : Number(e.target.value))}
                 data-testid="xero-cass-discount" />
        </Field>
        <Field label="CASS column heading"><input className={input} value={cfg.cass_price_label || ""} onChange={(e) => set("cass_price_label", e.target.value)} data-testid="xero-cass-label" /></Field>
      </div>
      <Field label="CASS price note" hint="Small print under the table explaining the discounted column.">
        <textarea className={input} rows={2} value={cfg.cass_price_note || ""} onChange={(e) => set("cass_price_note", e.target.value)} data-testid="xero-cass-note" />
      </Field>
      <Field label="Support caveat" hint="Shown wherever the page mentions CASS support.">
        <textarea className={input} rows={2} value={cfg.support_caveat || ""} onChange={(e) => set("support_caveat", e.target.value)} data-testid="xero-support-caveat-field" />
      </Field>
      <Field label="Partner line" hint="The saving is now shown in the table too.">
        <textarea className={input} rows={2} value={cfg.partner_note || ""} onChange={(e) => set("partner_note", e.target.value)} data-testid="xero-partner-note" />
      </Field>
      <Field label="How billing works"><textarea className={input} rows={2} value={cfg.billing_note || ""} onChange={(e) => set("billing_note", e.target.value)} /></Field>
      <Field label="Button label"><input className={input} value={cfg.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} /></Field>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-navy">Which plan do I need? guide</h3>
          <button type="button" onClick={() => setGuide("steps", [...(guide.steps || []), { q: "", a: "" }])}
                  className="inline-flex items-center gap-1.5 text-orange font-semibold text-sm"><Plus size={15} /> Add question</button>
        </div>
        <div className="grid gap-4 md:grid-cols-2 mb-3">
          <Field label="Heading"><input className={input} value={guide.heading || ""} onChange={(e) => setGuide("heading", e.target.value)} data-testid="xero-guide-heading" /></Field>
          <Field label="Intro"><input className={input} value={guide.intro || ""} onChange={(e) => setGuide("intro", e.target.value)} /></Field>
        </div>
        <div className="space-y-2">
          {(guide.steps || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`xero-guide-row-${i}`}>
              <div className="flex gap-2">
                <input className={input} placeholder="The question that decides it" value={s.q || ""} onChange={(e) => setStep(i, "q", e.target.value)} />
                <button type="button" onClick={() => setGuide("steps", guide.steps.filter((_, idx) => idx !== i))}
                        className="p-2.5 text-slate-400 hover:text-red-500 transition-colors" aria-label="Remove question"><Trash2 size={16} /></button>
              </div>
              <textarea className={input} rows={2} placeholder="What it means for the plan they need" value={s.a || ""} onChange={(e) => setStep(i, "a", e.target.value)} />
            </div>
          ))}
        </div>
        <Field label="Closing note"><textarea className={input} rows={2} value={guide.note || ""} onChange={(e) => setGuide("note", e.target.value)} /></Field>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-navy">Cost FAQs</h3>
          <button type="button" onClick={() => set("faqs", [...cfg.faqs, { ...blankFaq }])}
                  className="inline-flex items-center gap-1.5 text-orange font-semibold text-sm"><Plus size={15} /> Add question</button>
        </div>
        <div className="space-y-3">
          {(cfg.faqs || []).map((f, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`xero-faq-row-${i}`}>
              <div className="flex gap-2">
                <input className={input} placeholder="Question" value={f.q || ""} onChange={(e) => setFaq(i, "q", e.target.value)} />
                <button type="button" onClick={() => set("faqs", cfg.faqs.filter((_, idx) => idx !== i))}
                        className="p-2.5 text-slate-400 hover:text-red-500 transition-colors" aria-label="Remove question"><Trash2 size={16} /></button>
              </div>
              <textarea className={input} rows={3} placeholder="Answer" value={f.a || ""} onChange={(e) => setFaq(i, "a", e.target.value)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
